/**
 * West Virginia Home Health Agency Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary Federal Regulation: 42 CFR Part 484 (Medicare Conditions of Participation)
 * - Background Checks: W. Va. Code §16-49 (WV CARES Act) and 69 CSR 10
 * - Home Health Services: W. Va. Code §16-2C
 * - Certificate of Need: W. Va. Code §16-2D
 * - Deemed Status: W. Va. Code §16-5M-3(b)
 * - HIPAA: 45 CFR Parts 160, 162, 164
 * - OSHA: 29 CFR 1910
 * 
 * IMPORTANT NOTE: Medicare-certified home health agencies in West Virginia
 * operate under federal 42 CFR 484 Conditions of Participation with "deemed status"
 * per W. Va. Code §16-5M-3(b). State-specific requirements primarily apply to
 * background checks (WV CARES) and Certificate of Need for new agencies.
 * 
 * REGULATORY BODY:
 * Office of Health Facility Licensure and Certification (OHFLAC)
 * 1 Players Club Dr., Suite 301, Charleston, WV 25311
 * Phone: (304) 558-0050
 * Home Health Hotline: (800) 442-2888
 * Website: https://ohflac.wvdhhr.org
 * 
 * WV CARES Contact:
 * Email: wvcares@wv.gov
 * Phone: (304) 558-2278
 * 
 * KEY REGULATORY CITATIONS:
 * - 42 CFR 484.1-484.115: Medicare Conditions of Participation for HHAs
 * - W. Va. Code §16-49: WV CARES (Clearance for Access: Registry and Employment Screening)
 * - 69 CSR 10: WV CARES Legislative Rule
 * - W. Va. Code §16-2D-8: Certificate of Need Requirements
 * - 42 CFR 484.80: Home Health Aide Services (75-hour training, 14-day supervision)
 * - 42 CFR 484.55: Comprehensive Assessment (OASIS)
 * - 42 CFR 484.60: Care Planning
 * - 42 CFR 484.65: QAPI Requirements
 * - 42 CFR 484.102: Emergency Preparedness
 * 
 * Last Updated: December 2024
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const WESTVIRGINIA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: AGENCY LICENSURE & CERTIFICATION
  // ============================================
  {
    id: 'WV-A1',
    itemId: 'A1',
    stateCode: 'WV',
    title: 'Medicare Certification Status',
    description: 'Medicare certification establishes deemed status for West Virginia home health agencies per W. Va. Code §16-5M-3(b)',
    severity: 'high',
    regulationCode: '42 CFR 484.1',
    regulationName: 'Medicare Certification',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency hold current Medicare certification from CMS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Medicare certification is required to provide home health services to Medicare beneficiaries and establishes deemed status for state survey purposes per W. Va. Code §16-5M-3(b).',
      },
      {
        id: 'q2',
        question: 'Is your Medicare certification current with no conditions or plans of correction?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, current with no conditions' },
          { value: 'no', label: 'No, has conditions or POC' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Conditions or plans of correction may indicate compliance issues requiring attention.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency maintains valid Medicare certification establishing deemed status in West Virginia.',
      atRisk: 'Medicare certification exists but conditions or plan of correction require attention.',
      nonCompliant: 'Agency lacks required Medicare certification for home health services.',
      unknown: 'Medicare certification status requires verification.',
    },
    stateNotes: [
      'Medicare-certified agencies have deemed status per W. Va. Code §16-5M-3(b)',
      'OHFLAC conducts surveys on behalf of CMS',
      'Certification required to bill Medicare/Medicaid for home health services',
    ],
  },

  {
    id: 'WV-A2',
    itemId: 'A2',
    stateCode: 'WV',
    title: 'Certificate of Need (CON)',
    description: 'New home health agencies in West Virginia require Certificate of Need under W. Va. Code §16-2D-8',
    severity: 'high',
    regulationCode: '16-2D-8(a)(1)',
    regulationName: 'Certificate of Need',
    agencyName: 'West Virginia Health Care Authority',
    agencyAbbr: 'WVHCA',
    agencyPhone: '(304) 558-7000',
    agencyUrl: 'https://hca.wv.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency hold a Certificate of Need or predate the CON requirement?',
        inputType: 'select',
        options: [
          { value: 'yes_con', label: 'Yes, valid CON on file' },
          { value: 'grandfathered', label: 'Agency predates CON requirement (grandfathered)' },
          { value: 'pending', label: 'CON application pending' },
          { value: 'no', label: 'No CON and not grandfathered' },
        ],
        required: true,
        helpText: 'CON required for establishment of new home health agencies per W. Va. Code §16-2D-8(a)(1).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes_con', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'grandfathered', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency holds required Certificate of Need or is grandfathered.',
      atRisk: 'CON application pending with Health Care Authority.',
      nonCompliant: 'Agency operating without required Certificate of Need.',
      unknown: 'Certificate of Need status requires verification.',
    },
    stateNotes: [
      'CON required for establishment of new home health agencies per W. Va. Code §16-2D-8(a)(1)',
      'Agencies established before CON requirement may be grandfathered',
      'Health Care Authority reviews and approves CON applications',
    ],
  },

  {
    id: 'WV-A3',
    itemId: 'A3',
    stateCode: 'WV',
    title: 'Administrator Qualifications',
    description: 'Home health agency administrators must meet federal qualification requirements per 42 CFR 484.115(a)',
    severity: 'high',
    regulationCode: '42 CFR 484.115(a)',
    regulationName: 'Administrator',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'When was your current administrator hired?',
        inputType: 'select',
        options: [
          { value: 'before_2018', label: 'Before January 13, 2018' },
          { value: 'after_2018', label: 'On or after January 13, 2018' },
        ],
        required: true,
        helpText: 'Administrator qualification requirements differ based on hire date per 42 CFR 484.115(a).',
      },
      {
        id: 'q2',
        question: 'Does your administrator meet all qualification requirements with documentation on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Before 1/13/2018: Licensed physician, RN, or training/experience with 1+ year supervisory. On/after 1/13/2018: Licensed physician, RN, or undergraduate degree plus 1+ year supervisory.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets all federal qualification requirements per 42 CFR 484.115(a).',
      atRisk: 'Administrator qualifications need documentation.',
      nonCompliant: 'Administrator does not meet federal qualification requirements.',
      unknown: 'Administrator qualifications require verification.',
    },
    stateNotes: [
      'Before 1/13/2018: Licensed physician, RN, or training/experience with 1+ year supervisory experience',
      'On/after 1/13/2018: Licensed physician, RN, or undergraduate degree plus 1+ year supervisory experience',
      'Documentation must be maintained in personnel file',
    ],
  },

  {
    id: 'WV-A4',
    itemId: 'A4',
    stateCode: 'WV',
    title: 'Governing Body',
    description: 'Agency must have a governing body exercising full legal authority per 42 CFR 484.105',
    severity: 'high',
    regulationCode: '42 CFR 484.105',
    regulationName: 'Governing Body',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an established governing body with documented bylaws and meeting minutes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Governing body assumes full legal authority and responsibility for agency operations per 42 CFR 484.105.',
      },
      {
        id: 'q2',
        question: 'How often does the governing body meet?',
        inputType: 'select',
        options: [
          { value: 'quarterly', label: 'Quarterly or more frequently' },
          { value: 'annual', label: 'At least annually' },
          { value: 'rarely', label: 'Rarely or never' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Regular meetings demonstrate active oversight of agency operations.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body established with documented authority and regular oversight.',
      atRisk: 'Governing body exists but requires improved documentation.',
      nonCompliant: 'No established governing body as required by federal regulations.',
      unknown: 'Governing body status requires verification.',
    },
    stateNotes: [
      'Governing body assumes full legal authority per 42 CFR 484.105',
      'Must appoint qualified administrator',
      'Responsible for ensuring compliance with all applicable laws',
    ],
  },

  // ============================================
  // SECTION B: WV CARES BACKGROUND SCREENING
  // ============================================
  {
    id: 'WV-B1',
    itemId: 'B1',
    stateCode: 'WV',
    title: 'WV CARES System Registration',
    description: 'Agencies must register with WV CARES to conduct required background checks per W. Va. Code §16-49',
    severity: 'high',
    regulationCode: '16-49-3',
    regulationName: 'WV CARES',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency registered in the WV CARES system with an active administrative account?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Contact WV CARES at wvcares@wv.gov or (304) 558-2278 to set up administrative account. Registration required before conducting any employee background checks.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency maintains active WV CARES registration for background screening.',
      atRisk: 'WV CARES registration pending.',
      nonCompliant: 'Agency not registered with WV CARES as required by W. Va. Code §16-49.',
      unknown: 'WV CARES registration status requires verification.',
    },
    stateNotes: [
      'Contact WV CARES at wvcares@wv.gov or (304) 558-2278',
      'Registration required before conducting any employee background checks',
      'Home health agencies are covered providers under W. Va. Code §16-49',
    ],
  },

  {
    id: 'WV-B2',
    itemId: 'B2',
    stateCode: 'WV',
    title: 'Mandatory Prescreening',
    description: 'All direct access personnel must be prescreened through WV CARES before hire',
    severity: 'high',
    regulationCode: '16-49-3(b)',
    regulationName: 'Prescreening',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct WV CARES prescreening for all direct access personnel before hiring?',
        inputType: 'select',
        options: [
          { value: 'yes_all', label: 'Yes, for all direct access personnel before hire' },
          { value: 'yes_most', label: 'Yes, but some employees were hired without prescreening' },
          { value: 'no', label: 'No, prescreening not consistently conducted' },
        ],
        required: true,
        helpText: 'Prescreening includes OIG Exclusion List, WV Sex Offender Registry, NCIC, and other registries. Cannot hire until prescreening is cleared.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes_all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes_most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All direct access personnel prescreened through WV CARES before hire.',
      atRisk: 'Prescreening conducted but gaps exist in process.',
      nonCompliant: 'Mandatory prescreening not consistently performed before hiring.',
      unknown: 'Prescreening practices require verification.',
    },
    stateNotes: [
      'Prescreening includes OIG Exclusion List, WV Sex Offender Registry, NCIC, and other registries',
      'Cannot hire until prescreening is cleared',
      'Prescreening is separate from fingerprint background check',
    ],
  },

  {
    id: 'WV-B3',
    itemId: 'B3',
    stateCode: 'WV',
    title: 'Fingerprint-Based Background Checks',
    description: 'State and federal fingerprint-based criminal background checks required through WV CARES',
    severity: 'high',
    regulationCode: '16-49-3(c)',
    regulationName: 'Background Checks',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Do all direct access personnel have fingerprint-based background checks on file?',
        inputType: 'select',
        options: [
          { value: 'yes_all', label: 'Yes, all have completed checks with fitness determination' },
          { value: 'yes_pending', label: 'Yes, but some are within 60-day provisional period' },
          { value: 'partial', label: 'Some employees missing background checks' },
          { value: 'no', label: 'No fingerprint background checks conducted' },
        ],
        required: true,
        helpText: 'Fingerprint checks processed through WV State Police and FBI. 60-day provisional employment allowed while awaiting fitness determination.',
      },
      {
        id: 'q2',
        question: 'Are fitness determination documents maintained in all personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Fitness determination document must be maintained in personnel file.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes_all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes_pending', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All direct access personnel have required fingerprint background checks.',
      atRisk: 'Background checks incomplete for some employees.',
      nonCompliant: 'Required fingerprint background checks not conducted.',
      unknown: 'Background check status requires verification.',
    },
    stateNotes: [
      'Fingerprint checks processed through WV State Police and FBI',
      '60-day provisional employment allowed while awaiting fitness determination',
      'Fitness determination document must be maintained in personnel file',
    ],
  },

  {
    id: 'WV-B4',
    itemId: 'B4',
    stateCode: 'WV',
    title: 'Provisional Employee Supervision',
    description: 'Provisional employees must be supervised by staff who have cleared background checks',
    severity: 'high',
    regulationCode: '16-49-3(d)',
    regulationName: 'Provisional Employment',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Are provisional employees (awaiting fitness determination) supervised by cleared staff?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, always supervised with documentation' },
          { value: 'partial', label: 'Usually supervised but not always documented' },
          { value: 'no', label: 'No, provisional employees work unsupervised' },
          { value: 'na', label: 'N/A - No provisional employees currently' },
        ],
        required: true,
        helpText: 'Maximum 60 days provisional employment. Supervisor must have cleared all required background checks.',
      },
      {
        id: 'q2',
        question: 'Do any employees exceed the 60-day provisional employment limit?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No, all within 60-day limit or fully cleared' },
          { value: 'yes', label: 'Yes, some employees have exceeded 60 days' },
        ],
        required: true,
        helpText: 'Employees cannot work beyond 60-day provisional period without fitness determination.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Provisional employees properly supervised by cleared staff within 60-day limit.',
      atRisk: 'Supervision practices need improvement or documentation.',
      nonCompliant: 'Provisional employees working unsupervised or beyond 60-day limit.',
      unknown: 'Provisional employment practices require verification.',
    },
    stateNotes: [
      'Maximum 60 days provisional employment while awaiting fitness determination',
      'Supervisor must have cleared all required background checks',
      'Supervision must be documented',
    ],
  },

  {
    id: 'WV-B5',
    itemId: 'B5',
    stateCode: 'WV',
    title: 'Rap Back Enrollment',
    description: 'Employees must be enrolled in Rap Back for ongoing criminal history monitoring',
    severity: 'medium',
    regulationCode: '16-49-3(e)',
    regulationName: 'Rap Back',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Are all direct access employees enrolled in the WV CARES Rap Back program?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all enrolled with active monitoring' },
          { value: 'partial', label: 'Most enrolled but some gaps exist' },
          { value: 'no', label: 'No, Rap Back enrollment not maintained' },
        ],
        required: true,
        helpText: 'Rap Back provides automatic notification of subsequent criminal activity. Enrollment maintained through WV CARES system.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All employees enrolled in Rap Back for ongoing monitoring.',
      atRisk: 'Rap Back enrollment incomplete for some employees.',
      nonCompliant: 'Rap Back enrollment not maintained as required.',
      unknown: 'Rap Back enrollment status requires verification.',
    },
    stateNotes: [
      'Rap Back provides automatic notification of subsequent criminal activity',
      'Eliminates need for re-fingerprinting when changing employers within industry',
      'Enrollment maintained through WV CARES system',
    ],
  },

  {
    id: 'WV-B6',
    itemId: 'B6',
    stateCode: 'WV',
    title: 'Out-of-State Background Checks',
    description: 'Employees with out-of-state residence in past 5 years require additional checks',
    severity: 'medium',
    regulationCode: '16-49-3',
    regulationName: 'Interstate Checks',
    agencyName: 'WV CARES Unit',
    agencyAbbr: 'WV CARES',
    agencyPhone: '(304) 558-2278',
    agencyUrl: 'https://www.wvdhhr.org/oig/wvcares.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify 5-year residence history for all new employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'State checks required for any state of residence in past 5 years.',
      },
      {
        id: 'q2',
        question: 'For employees with out-of-state residence, are appropriate state checks conducted?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, state checks conducted for all prior states' },
          { value: 'fbi_only', label: 'FBI check only (for National Fingerprint File states)' },
          { value: 'partial', label: 'Some out-of-state checks but not all' },
          { value: 'no', label: 'No out-of-state checks conducted' },
          { value: 'na', label: 'N/A - All employees are WV residents for 5+ years' },
        ],
        helpText: 'FBI fingerprint check may satisfy requirement for National Fingerprint File (NFF) states. West Virginia is a Compact State participating in NFF.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'fbi_only', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'na', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Out-of-state background checks appropriately conducted.',
      atRisk: 'Out-of-state verification process needs improvement.',
      nonCompliant: 'Required out-of-state background checks not conducted.',
      unknown: 'Out-of-state check practices require verification.',
    },
    stateNotes: [
      'State checks required for any state of residence in past 5 years',
      'FBI fingerprint check may satisfy requirement for National Fingerprint File (NFF) states',
      'West Virginia is a Compact State participating in NFF',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL QUALIFICATIONS
  // ============================================
  {
    id: 'WV-C1',
    itemId: 'C1',
    stateCode: 'WV',
    title: 'Registered Nurse Licensure',
    description: 'All RNs must hold current West Virginia or compact state licensure',
    severity: 'high',
    regulationCode: '42 CFR 484.115(b)',
    regulationName: 'RN Licensure',
    agencyName: 'WV Board of Examiners for Registered Professional Nurses',
    agencyAbbr: 'WV RN Board',
    agencyPhone: '(304) 744-0900',
    agencyUrl: 'https://wvrnboard.wv.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all RNs hold current, unencumbered West Virginia or compact state licenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'West Virginia is a Nurse Licensure Compact (NLC) state. Compact licenses from other NLC states valid in WV.',
      },
      {
        id: 'q2',
        question: 'Do you have a system for tracking RN license expirations?',
        inputType: 'select',
        options: [
          { value: 'automated', label: 'Yes, automated tracking with advance reminders' },
          { value: 'manual', label: 'Yes, manual tracking system' },
          { value: 'no', label: 'No formal tracking system' },
        ],
        helpText: 'License verification available at wvrnboard.wv.gov.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All RN licenses verified current and unencumbered.',
      atRisk: 'Some RN license verifications pending.',
      nonCompliant: 'RNs practicing with expired or restricted licenses.',
      unknown: 'RN licensure status requires verification.',
    },
    stateNotes: [
      'West Virginia is a Nurse Licensure Compact (NLC) state',
      'Compact licenses from other NLC states valid in WV',
      'License verification available at wvrnboard.wv.gov',
    ],
  },

  {
    id: 'WV-C2',
    itemId: 'C2',
    stateCode: 'WV',
    title: 'LPN Licensure and Supervision',
    description: 'LPNs must be licensed and work under RN supervision per 42 CFR 484.115(e)',
    severity: 'high',
    regulationCode: '42 CFR 484.115(e)',
    regulationName: 'LPN Licensure',
    agencyName: 'WV Board of Examiners for Licensed Practical Nurses',
    agencyAbbr: 'WV LPN Board',
    agencyPhone: '(304) 558-3572',
    agencyUrl: 'https://wvlpnboard.wv.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do all LPNs hold current West Virginia or compact state licenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'West Virginia participates in Nurse Licensure Compact.',
      },
      {
        id: 'q2',
        question: 'Do all LPNs work under documented RN supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'LPNs must furnish services under RN supervision per 42 CFR 484.115(e). Supervision must be documented.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All LPNs properly licensed and supervised by RNs.',
      atRisk: 'LPN licensure or supervision documentation needs improvement.',
      nonCompliant: 'LPNs practicing with license issues or without RN supervision.',
      unknown: 'LPN licensure and supervision require verification.',
    },
    stateNotes: [
      'LPNs must furnish services under RN supervision per 42 CFR 484.115(e)',
      'West Virginia participates in Nurse Licensure Compact',
      'Supervision must be documented in personnel and patient records',
    ],
  },

  {
    id: 'WV-C3',
    itemId: 'C3',
    stateCode: 'WV',
    title: 'Therapy Personnel Qualifications',
    description: 'Physical therapists, occupational therapists, and speech-language pathologists must meet licensure requirements',
    severity: 'high',
    regulationCode: '42 CFR 484.115(f)',
    regulationName: 'Therapy Personnel',
    agencyName: 'Various WV Professional Boards',
    agencyAbbr: 'WV Boards',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Do all therapy personnel (PT, OT, SLP) hold current West Virginia licenses?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all licenses verified current' },
          { value: 'pending', label: 'Most current but some pending verification' },
          { value: 'issues', label: 'Some therapists have license issues' },
          { value: 'na', label: 'N/A - No therapy services provided' },
        ],
        required: true,
        helpText: 'PT: W. Va. Code §30-20; OT: W. Va. Code §30-28; SLP: W. Va. Code §30-32.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'issues', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All therapy personnel properly licensed.',
      atRisk: 'Some therapy personnel credentials need verification.',
      nonCompliant: 'Therapy personnel practicing without proper credentials.',
      unknown: 'Therapy personnel qualifications require verification.',
    },
    stateNotes: [
      'PT: W. Va. Code §30-20; OT: W. Va. Code §30-28; SLP: W. Va. Code §30-32',
      'Federal qualification requirements in 42 CFR 484.115',
      'Therapy assistants must work under appropriate supervision',
    ],
  },

  // ============================================
  // SECTION D: HOME HEALTH AIDE TRAINING
  // ============================================
  {
    id: 'WV-D1',
    itemId: 'D1',
    stateCode: 'WV',
    title: 'Home Health Aide 75-Hour Training',
    description: 'Home health aides must complete at least 75 hours of training per 42 CFR 484.80',
    severity: 'high',
    regulationCode: '42 CFR 484.80(b)',
    regulationName: 'HHA Training',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Have all home health aides completed at least 75 hours of training?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all have 75+ hours documented' },
          { value: 'partial', label: 'Most have completed but some have less than 75 hours' },
          { value: 'no', label: 'Training requirements not consistently met' },
        ],
        required: true,
        helpText: 'Minimum 75 hours total training per 42 CFR 484.80(b)(1). Alternative: State-approved nurse aide training program meeting 42 CFR 483.151-154.',
      },
      {
        id: 'q2',
        question: 'Does training include at least 16 hours classroom before 16 hours supervised practical?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '16 hours classroom must precede 16 hours supervised practical training per 42 CFR 484.80(b)(2).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides have completed required 75-hour training.',
      atRisk: 'Some home health aides need additional training hours.',
      nonCompliant: 'Home health aides providing services without required training.',
      unknown: 'HHA training documentation requires verification.',
    },
    stateNotes: [
      'Minimum 75 hours total training per 42 CFR 484.80(b)(1)',
      '16 hours classroom must precede 16 hours supervised practical training',
      'Alternative: State-approved nurse aide training program meeting 42 CFR 483.151-154',
    ],
  },

  {
    id: 'WV-D2',
    itemId: 'D2',
    stateCode: 'WV',
    title: 'HHA Competency Evaluation',
    description: 'All home health aides must pass competency evaluation by RN per 42 CFR 484.80(c)',
    severity: 'high',
    regulationCode: '42 CFR 484.80(c)',
    regulationName: 'HHA Competency',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Have all home health aides completed a competency evaluation conducted by an RN?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'RN must conduct competency evaluation per 42 CFR 484.80(c). Must address all required subject areas from training with written evaluation and skills demonstration.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HHAs have RN-conducted competency evaluations documented.',
      atRisk: 'Competency evaluation process needs improvement.',
      nonCompliant: 'HHAs providing services without competency evaluation.',
      unknown: 'Competency evaluation documentation requires verification.',
    },
    stateNotes: [
      'RN must conduct competency evaluation per 42 CFR 484.80(c)',
      'Must address all required subject areas from training',
      'Written evaluation and skills demonstration required',
    ],
  },

  {
    id: 'WV-D3',
    itemId: 'D3',
    stateCode: 'WV',
    title: 'HHA Annual In-Service Training',
    description: 'Home health aides require 12 hours annual in-service training per 42 CFR 484.80(d)',
    severity: 'high',
    regulationCode: '42 CFR 484.80(d)',
    regulationName: 'HHA In-Service',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Do all home health aides receive at least 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, 12+ hours documented annually for all HHAs' },
          { value: 'partial', label: 'Some HHAs below 12-hour requirement' },
          { value: 'no', label: 'Annual in-service training not provided' },
        ],
        required: true,
        helpText: 'Minimum 12 hours annually per 42 CFR 484.80(d). Training should address weaknesses identified during supervision.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All HHAs complete required 12 hours annual in-service training.',
      atRisk: 'Some HHAs below annual in-service training requirement.',
      nonCompliant: 'Annual in-service training not provided as required.',
      unknown: 'In-service training documentation requires verification.',
    },
    stateNotes: [
      'Minimum 12 hours annually per 42 CFR 484.80(d)',
      'Training should address weaknesses identified during supervision',
      'Documentation must include topics, hours, and dates',
    ],
  },

  {
    id: 'WV-D4',
    itemId: 'D4',
    stateCode: 'WV',
    title: 'HHA Supervisory Visits',
    description: 'RN must conduct on-site supervisory visits at least every 14 days per 42 CFR 484.80(h)',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)(1)',
    regulationName: 'HHA Supervision',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does an RN conduct on-site supervisory visits with HHAs at least every 14 days?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, 14-day supervision schedule maintained' },
          { value: 'partial', label: 'Supervision occurs but sometimes exceeds 14 days' },
          { value: 'no', label: 'No regular supervisory visit schedule' },
        ],
        required: true,
        helpText: 'RN must make on-site visit to patient home while aide is present per 42 CFR 484.80(h)(1).',
      },
      {
        id: 'q2',
        question: 'Are supervisory visits conducted while the aide is present with the patient?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Visit must assess aide performance and relationship with patient. Documentation required in patient record.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervisory visits conducted every 14 days with aide present.',
      atRisk: 'Supervisory visit frequency needs improvement.',
      nonCompliant: 'Required 14-day supervisory visits not being conducted.',
      unknown: 'Supervisory visit practices require verification.',
    },
    stateNotes: [
      'RN must make on-site visit to patient home while aide is present per 42 CFR 484.80(h)(1)',
      'Visit must assess aide performance and relationship with patient',
      'Documentation required in patient record',
    ],
  },

  // ============================================
  // SECTION E: PATIENT CARE & CLINICAL SERVICES
  // ============================================
  {
    id: 'WV-E1',
    itemId: 'E1',
    stateCode: 'WV',
    title: 'Initial Assessment Timeliness',
    description: 'RN must conduct initial assessment within 48 hours of referral per 42 CFR 484.55(a)',
    severity: 'high',
    regulationCode: '42 CFR 484.55(a)(1)',
    regulationName: 'Initial Assessment',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Is an RN initial assessment conducted within 48 hours of referral or return home?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, consistently within 48 hours' },
          { value: 'partial', label: 'Usually within 48 hours but some delays' },
          { value: 'no', label: 'Initial assessments often delayed beyond 48 hours' },
        ],
        required: true,
        helpText: 'RN initial assessment within 48 hours per 42 CFR 484.55(a)(1). Determines immediate care needs and Medicare homebound status.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN initial assessments conducted within 48 hours.',
      atRisk: 'Initial assessment timeliness needs improvement.',
      nonCompliant: 'Initial assessments not conducted within required 48-hour timeframe.',
      unknown: 'Initial assessment timeliness requires verification.',
    },
    stateNotes: [
      'RN initial assessment within 48 hours per 42 CFR 484.55(a)(1)',
      'Determines immediate care and support needs',
      'For Medicare: verifies homebound status and eligibility',
    ],
  },

  {
    id: 'WV-E2',
    itemId: 'E2',
    stateCode: 'WV',
    title: 'Comprehensive Assessment (OASIS)',
    description: 'Comprehensive assessment must be completed within 5 days of start of care per 42 CFR 484.55(b)',
    severity: 'high',
    regulationCode: '42 CFR 484.55(b)(1)',
    regulationName: 'Comprehensive Assessment',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Is the comprehensive assessment completed within 5 calendar days of start of care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Comprehensive assessment within 5 days per 42 CFR 484.55(b)(1).',
      },
      {
        id: 'q2',
        question: 'Is OASIS data transmitted to CMS within 30 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'OASIS transmission within 30 days per 42 CFR 484.45(b).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Comprehensive assessments completed within 5 days with timely OASIS transmission.',
      atRisk: 'Assessment timeliness needs improvement.',
      nonCompliant: 'Comprehensive assessments not completed within required timeframes.',
      unknown: 'Assessment practices require verification.',
    },
    stateNotes: [
      'Comprehensive assessment within 5 days per 42 CFR 484.55(b)(1)',
      'OASIS transmission within 30 days per 42 CFR 484.45(b)',
      'RN must complete comprehensive assessment for Medicare patients',
    ],
  },

  {
    id: 'WV-E3',
    itemId: 'E3',
    stateCode: 'WV',
    title: 'Physician-Signed Plan of Care',
    description: 'Each patient must have physician-signed individualized plan of care per 42 CFR 484.60(a)',
    severity: 'high',
    regulationCode: '42 CFR 484.60(a)',
    regulationName: 'Plan of Care',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does each patient have a physician-signed plan of care?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all patients have signed POC' },
          { value: 'partial', label: 'Most have POC but some signatures pending' },
          { value: 'no', label: 'Many patients lack physician-signed POC' },
        ],
        required: true,
        helpText: 'Physician signature required per 42 CFR 484.60(a). Must include services, frequency, goals, medications, discharge planning.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All patients have physician-signed plans of care.',
      atRisk: 'Some plans of care missing signatures.',
      nonCompliant: 'Patients receiving services without proper plans of care.',
      unknown: 'Plan of care documentation requires verification.',
    },
    stateNotes: [
      'Physician signature required per 42 CFR 484.60(a)',
      'Must include services, frequency, goals, medications, discharge planning',
      'Verbal orders must be authenticated per agency policy',
    ],
  },

  // ============================================
  // SECTION F: INFECTION CONTROL & SAFETY
  // ============================================
  {
    id: 'WV-F1',
    itemId: 'F1',
    stateCode: 'WV',
    title: 'Infection Prevention and Control Program',
    description: 'Agency must maintain infection prevention and control program per 42 CFR 484.70',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain an infection prevention and control program?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, comprehensive program with policies and training' },
          { value: 'partial', label: 'Program exists but incomplete' },
          { value: 'no', label: 'No formal infection control program' },
        ],
        required: true,
        helpText: 'Follow accepted standards of practice per 42 CFR 484.70. Includes hand hygiene, PPE, standard precautions, and surveillance.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Comprehensive infection prevention and control program in place.',
      atRisk: 'Infection control program needs enhancement.',
      nonCompliant: 'No formal infection prevention and control program.',
      unknown: 'Infection control practices require verification.',
    },
    stateNotes: [
      'Follow accepted standards of practice per 42 CFR 484.70',
      'Includes hand hygiene, PPE, standard precautions',
      'Staff training and surveillance required',
    ],
  },

  {
    id: 'WV-F2',
    itemId: 'F2',
    stateCode: 'WV',
    title: 'TB Screening',
    description: 'All patient-contact staff must have TB screening per CDC guidelines',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'TB Screening',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Do all patient-contact staff have current TB screening on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'TB screening at hire and periodically per CDC guidelines. Documentation must be maintained in personnel files.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All patient-contact staff have current TB screening.',
      atRisk: 'Some staff TB screenings overdue.',
      nonCompliant: 'TB screening not consistently performed.',
      unknown: 'TB screening status requires verification.',
    },
    stateNotes: [
      'TB screening at hire and periodically per CDC guidelines',
      'Documentation must be maintained in personnel files',
      'Follow-up required for positive results',
    ],
  },

  {
    id: 'WV-F3',
    itemId: 'F3',
    stateCode: 'WV',
    title: 'Bloodborne Pathogen Exposure Control',
    description: 'Agency must have OSHA-compliant bloodborne pathogen exposure control plan',
    severity: 'high',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written bloodborne pathogen exposure control plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Written exposure control plan required per 29 CFR 1910.1030. Must include annual training for at-risk employees.',
      },
      {
        id: 'q2',
        question: 'Is Hepatitis B vaccination offered to all employees with exposure risk?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Hepatitis B vaccination must be offered at no cost per 29 CFR 1910.1030(f).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OSHA-compliant bloodborne pathogen program in place.',
      atRisk: 'Exposure control plan or training needs updating.',
      nonCompliant: 'No bloodborne pathogen exposure control plan.',
      unknown: 'Exposure control practices require verification.',
    },
    stateNotes: [
      'Written exposure control plan required per 29 CFR 1910.1030',
      'Hepatitis B vaccination must be offered at no cost',
      'Annual training required for all employees with exposure risk',
    ],
  },

  // ============================================
  // SECTION G: EMERGENCY PREPAREDNESS
  // ============================================
  {
    id: 'WV-G1',
    itemId: 'G1',
    stateCode: 'WV',
    title: 'Emergency Preparedness Plan',
    description: 'Agency must have emergency preparedness plan based on risk assessment per 42 CFR 484.102',
    severity: 'high',
    regulationCode: '42 CFR 484.102(a)',
    regulationName: 'Emergency Prep',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written emergency preparedness plan based on risk assessment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'All-hazards risk assessment required per 42 CFR 484.102(a)(1). Plan must be reviewed and updated at least every 2 years.',
      },
      {
        id: 'q2',
        question: 'Does the plan include individual patient emergency plans?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Individual patient emergency plans required as part of patient assessment per 42 CFR 484.102(b)(1).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan in place with risk assessment.',
      atRisk: 'Emergency plan or risk assessment needs updating.',
      nonCompliant: 'No emergency preparedness plan.',
      unknown: 'Emergency preparedness status requires verification.',
    },
    stateNotes: [
      'All-hazards risk assessment required per 42 CFR 484.102(a)(1)',
      'Plan must be reviewed and updated at least every 2 years',
      'Individual patient emergency plans required as part of assessment',
    ],
  },

  {
    id: 'WV-G2',
    itemId: 'G2',
    stateCode: 'WV',
    title: 'Emergency Training and Exercises',
    description: 'Staff must be trained on emergency procedures with annual exercises per 42 CFR 484.102(d)',
    severity: 'medium',
    regulationCode: '42 CFR 484.102(d)',
    regulationName: 'Emergency Training',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Are staff trained on emergency procedures at hire and annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Training at hire and annually per 42 CFR 484.102(d)(1).',
      },
      {
        id: 'q2',
        question: 'Does the agency conduct emergency preparedness exercises annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Annual exercise required per 42 CFR 484.102(d)(2). Community-based exercise participation may satisfy requirement.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency training and annual exercises documented.',
      atRisk: 'Emergency training or exercises need improvement.',
      nonCompliant: 'Emergency training and testing not conducted.',
      unknown: 'Emergency training status requires verification.',
    },
    stateNotes: [
      'Training at hire and annually per 42 CFR 484.102(d)(1)',
      'Annual exercise required per 42 CFR 484.102(d)(2)',
      'Community-based exercise participation may satisfy requirement',
    ],
  },

  // ============================================
  // SECTION H: PATIENT RIGHTS & PRIVACY
  // ============================================
  {
    id: 'WV-H1',
    itemId: 'H1',
    stateCode: 'WV',
    title: 'Patient Rights Notification',
    description: 'Patients must be informed of their rights at admission per 42 CFR 484.50',
    severity: 'high',
    regulationCode: '42 CFR 484.50(a)',
    regulationName: 'Patient Rights',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Are patients informed of their rights verbally and in writing at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Rights must be provided before or during initial evaluation per 42 CFR 484.50(a). Must be in language patient understands.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights provided and documented at admission.',
      atRisk: 'Patient rights process needs improvement.',
      nonCompliant: 'Patient rights not consistently provided at admission.',
      unknown: 'Patient rights practices require verification.',
    },
    stateNotes: [
      'Rights must be provided before or during initial evaluation per 42 CFR 484.50(a)',
      'Must be in language and manner patient understands',
      'Documentation required in patient record',
    ],
  },

  {
    id: 'WV-H2',
    itemId: 'H2',
    stateCode: 'WV',
    title: 'HIPAA Notice of Privacy Practices',
    description: 'Patients must receive HIPAA-compliant Notice of Privacy Practices',
    severity: 'high',
    regulationCode: '45 CFR 164.520',
    regulationName: 'Privacy Notice',
    agencyName: 'Office for Civil Rights',
    agencyAbbr: 'HHS-OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/ocr',
    questions: [
      {
        id: 'q1',
        question: 'Do all patients receive a HIPAA Notice of Privacy Practices with signed acknowledgment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'NPP required per 45 CFR 164.520. Good faith effort to obtain acknowledgment required.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA Notice of Privacy Practices provided to all patients.',
      atRisk: 'NPP process needs improved documentation.',
      nonCompliant: 'HIPAA NPP not consistently provided.',
      unknown: 'NPP practices require verification.',
    },
    stateNotes: [
      'NPP required per 45 CFR 164.520',
      'Good faith effort to obtain acknowledgment required',
      'NPP must be updated when practices change',
    ],
  },

  {
    id: 'WV-H3',
    itemId: 'H3',
    stateCode: 'WV',
    title: 'Grievance Process',
    description: 'Patients must be informed of grievance process per 42 CFR 484.50(a)(3)',
    severity: 'medium',
    regulationCode: '42 CFR 484.50(a)(3)',
    regulationName: 'Grievances',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Is there a documented grievance process with complaint tracking?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Patients must be informed how to file complaints per 42 CFR 484.50(a)(3). Must include information on filing with OHFLAC: (800) 442-2888.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Grievance process in place with complaint tracking.',
      atRisk: 'Grievance process needs improvement.',
      nonCompliant: 'No formal grievance process.',
      unknown: 'Grievance process status requires verification.',
    },
    stateNotes: [
      'Patients must be informed how to file complaints per 42 CFR 484.50(a)(3)',
      'Must include OHFLAC Home Health Hotline: (800) 442-2888',
      'Agency must investigate and resolve complaints',
    ],
  },

  // ============================================
  // SECTION I: CLINICAL RECORDS
  // ============================================
  {
    id: 'WV-I1',
    itemId: 'I1',
    stateCode: 'WV',
    title: 'Clinical Record Content',
    description: 'Clinical records must contain all required documentation per 42 CFR 484.110',
    severity: 'high',
    regulationCode: '42 CFR 484.110(a)',
    regulationName: 'Clinical Records',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Do clinical records contain all services documented with dates and signatures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Records must document all services, dates, signatures per 42 CFR 484.110(a). Authentication required by person providing service.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required documentation.',
      atRisk: 'Clinical record documentation has some gaps.',
      nonCompliant: 'Clinical records missing required documentation.',
      unknown: 'Clinical record completeness requires review.',
    },
    stateNotes: [
      'Records must document all services, dates, signatures per 42 CFR 484.110(a)',
      'Must include comprehensive assessment and plan of care',
      'Authentication required by person providing service',
    ],
  },

  {
    id: 'WV-I2',
    itemId: 'I2',
    stateCode: 'WV',
    title: 'Clinical Record Retention',
    description: 'Clinical records must be retained for at least 5 years per 42 CFR 484.110(d)',
    severity: 'medium',
    regulationCode: '42 CFR 484.110(d)',
    regulationName: 'Record Retention',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Are clinical records retained for at least 5 years from discharge?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Minimum 5 years from discharge per 42 CFR 484.110(d). For minors: 3 years after patient reaches age of majority.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets 5-year requirement.',
      atRisk: 'Record retention policy needs clarification.',
      nonCompliant: 'Records not retained for required 5 years.',
      unknown: 'Record retention practices require verification.',
    },
    stateNotes: [
      'Minimum 5 years from discharge per 42 CFR 484.110(d)',
      'For minors: 3 years after patient reaches age of majority',
      'State laws may require longer retention',
    ],
  },

  {
    id: 'WV-I3',
    itemId: 'I3',
    stateCode: 'WV',
    title: 'Record Security and Privacy',
    description: 'Clinical records must be secured per HIPAA requirements',
    severity: 'high',
    regulationCode: '42 CFR 484.110(c)',
    regulationName: 'Record Security',
    agencyName: 'Office for Civil Rights',
    agencyAbbr: 'HHS-OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/ocr',
    questions: [
      {
        id: 'q1',
        question: 'Are clinical records secured against unauthorized access with documented controls?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HIPAA Security Rule requirements apply per 45 CFR 164.312. Electronic records require access controls, audit logs, encryption.',
      },
      {
        id: 'q2',
        question: 'Are Business Associate Agreements in place with all vendors handling PHI?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'BAAs required per 45 CFR 164.502(e) and 45 CFR 164.504(e).',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records secured with documented access controls and BAAs.',
      atRisk: 'Record security measures need improvement.',
      nonCompliant: 'Clinical records not adequately secured.',
      unknown: 'Record security practices require verification.',
    },
    stateNotes: [
      'HIPAA Security Rule requirements apply per 45 CFR 164.312',
      'Electronic records require access controls, audit logs, encryption',
      'BAAs required with all entities handling PHI',
    ],
  },

  // ============================================
  // SECTION J: QUALITY ASSESSMENT & PERFORMANCE IMPROVEMENT
  // ============================================
  {
    id: 'WV-J1',
    itemId: 'J1',
    stateCode: 'WV',
    title: 'QAPI Program',
    description: 'Agency must maintain effective QAPI program per 42 CFR 484.65',
    severity: 'high',
    regulationCode: '42 CFR 484.65(a)',
    regulationName: 'QAPI',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an effective QAPI program with documented activities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Data-driven QAPI program required per 42 CFR 484.65(a). Must use quality indicator data including OASIS and HHCAHPS.',
      },
      {
        id: 'q2',
        question: 'Are quality indicators tracked and analyzed?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Track OASIS-based quality measures per 42 CFR 484.65(b). Analyze trends and compare to benchmarks.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Effective QAPI program with documented activities.',
      atRisk: 'QAPI program needs enhancement.',
      nonCompliant: 'No QAPI program in place as required.',
      unknown: 'QAPI program status requires verification.',
    },
    stateNotes: [
      'Data-driven QAPI program required per 42 CFR 484.65(a)',
      'Must use quality indicator data including OASIS and HHCAHPS',
      'Performance improvement projects required for high-priority issues',
    ],
  },

  {
    id: 'WV-J2',
    itemId: 'J2',
    stateCode: 'WV',
    title: 'Adverse Event Tracking',
    description: 'Agency must track and analyze adverse events per 42 CFR 484.65(d)',
    severity: 'medium',
    regulationCode: '42 CFR 484.65(d)',
    regulationName: 'Adverse Events',
    agencyName: 'Office of Health Facility Licensure and Certification',
    agencyAbbr: 'OHFLAC',
    agencyPhone: '(304) 558-0050',
    agencyUrl: 'https://ohflac.wvdhhr.org',
    questions: [
      {
        id: 'q1',
        question: 'Are adverse events tracked with root cause analysis and corrective actions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Track and analyze adverse events per 42 CFR 484.65(d). Conduct root cause analysis and implement corrective actions.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Adverse events tracked with root cause analysis.',
      atRisk: 'Adverse event analysis needs improvement.',
      nonCompliant: 'No adverse event tracking system.',
      unknown: 'Adverse event practices require verification.',
    },
    stateNotes: [
      'Track and analyze adverse events per 42 CFR 484.65(d)',
      'Conduct root cause analysis',
      'Implement corrective actions to prevent recurrence',
    ],
  },
];

export default WESTVIRGINIA_QUESTIONS;